import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { Search } from '@shared/components/ui/Search';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAuthStore } from '@shared/stores/authStore';
import {
  BulkInboxCourierFilesModal,
  CourierFilesAgGridTable,
  CourierFilesTopBarActions,
  ErrorState,
  ExportButton,
  FiltersSection,
  LoadingState,
  MarkProcessedButton,
  SelectionToolbar,
  StatusToolbar,
  RunInboxCourierFilesModal,
  UploadCourierFileButton,
} from '../';
import type { CourierFileStatus } from '../interaction';
import { useCourierFiles, useCourierFilesMain, useDownloadCourierFile } from '../../hooks';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';

export const Main = () => {
  const { t } = useTranslation('codCourierFiles');
  const { canAccess } = usePermissions();
  const team = useAuthStore((s) => s.team);

  const {
    filters,
    updateFilters,
    resetFilters,
    handleSortingChange,
    handleSearch,
    searchTerm,
    sorting,
    selectedFiles,
    handleSelectionChanged,
    handleClearSelection,
  } = useCourierFilesMain();

  const {
    courierFiles,
    total: totalFromApi,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCourierFiles({
    filters,
    sorting,
  });

  const selectedFilesData = useMemo(
    () => (courierFiles || []).filter((file) => selectedFiles.has(file.id)),
    [courierFiles, selectedFiles],
  );

  const confirmableCount = useMemo(
    () =>
      selectedFilesData.filter((file) => {
        const normalizedStatus = (file.status || '').toUpperCase();
        return normalizedStatus === 'IMPORTED';
      }).length,
    [selectedFilesData],
  );

  // Hook para descargar archivos
  const { downloadFile, downloadingFileId } = useDownloadCourierFile();

  const { columnDefs } = useAgGridColumns({
    onDownload: downloadFile,
    downloadingFileId,
  });

  // 🎯 Estado de carga inicial (solo mostrar loader completo la primera vez)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Estado para el tab activo de status
  const [activeStatus, setActiveStatus] = useState<CourierFileStatus>('all');

  // 🎯 Estado local para el input de búsqueda
  const [inputValue, setInputValue] = useState(searchTerm);

  // Sincronizar con searchTerm externo
  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  // 🎯 Handler para cambiar el status desde las tabs
  const handleStatusChange = useCallback(
    (status: CourierFileStatus) => {
      setActiveStatus(status);
      updateFilters({ status: status === 'all' ? undefined : status });
    },
    [updateFilters],
  );

  // Handler para cambio en el input de búsqueda
  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  }, []);

  // Handler para ejecutar búsqueda (Enter)
  const handleSearchSubmit = useCallback(
    (value?: string) => {
      const searchValue = value !== undefined ? value : inputValue;
      const trimmedValue = searchValue.trim();
      handleSearch(trimmedValue);
    },
    [inputValue, handleSearch],
  );

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Configurar breadcrumbs
  const breadcrumbs = useMemo(() => [{ label: t('breadcrumb.courier_files', 'Archivos Courier') }], [t]);

  // Estados para controlar los modales
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBulkInboxModalOpen, setIsBulkInboxModalOpen] = useState(false);
  const [isRunInboxModalOpen, setIsRunInboxModalOpen] = useState(false);
  const [isMarkProcessedModalOpen, setIsMarkProcessedModalOpen] = useState(false);

  // Handler para confirmar archivos seleccionados
  const handleConfirmSelected = useCallback(() => {
    if (confirmableCount > 0) {
      setIsMarkProcessedModalOpen(true);
    }
  }, [confirmableCount]);

  // Handler para cuando se cierra el modal de marcar como procesado
  const handleMarkProcessedClose = useCallback(() => {
    setIsMarkProcessedModalOpen(false);
    handleClearSelection();
  }, [handleClearSelection]);

  // 🎯 Verificar permisos de archivos courier
  const canUpload = canAccess(['tenant_3pl'], ['cod-payments.files.upload']);

  const handleOpenRunInbox = useCallback(() => {
    if (team?.id == null) return;
    setIsRunInboxModalOpen(true);
  }, [team?.id]);

  // Configurar acciones del header (mismo patrón que Invoices: un slot con componente agrupado)
  const headerActions = useMemo(() => {
    if (!canUpload) return [];
    return [
      {
        id: 'courier-files-topbar-actions',
        label: '',
        onClick: () => {},
        component: (
          <CourierFilesTopBarActions
            onOpenUpload={() => setIsUploadModalOpen(true)}
            onOpenBulkInbox={() => setIsBulkInboxModalOpen(true)}
            onOpenRunInbox={handleOpenRunInbox}
            canRunInbox={team?.id != null}
          />
        ),
      },
    ];
  }, [canUpload, handleOpenRunInbox, team?.id]);

  // Establecer breadcrumbs y acciones en la topbar
  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // Estado de carga inicial (solo la primera vez)
  if (isInitialLoad && isLoading) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.files', 'Cargando archivos...')} />
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={error} onRetry={refetch} />
      </div>
    );
  }

  // Total de archivos (API puede devolver meta en snake_case o camelCase; el hook lo normaliza)
  const total = totalFromApi;
  const hasFilters = filters.date_from || filters.date_to || filters.courier_id || filters.search;

  return (
    <div className="px-6 pt-4">
      {/* Buscador, Filtros, total y exportar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Search
            placeholder={t('filters.search_placeholder', 'Buscar por nombre de archivo...')}
            value={inputValue}
            onChange={handleInputChange}
            onEnter={handleSearchSubmit}
            className="w-64"
            dataTestId="courier-files-search"
            inputDataTestId="courier-files-search-input"
          />
          <FiltersSection filters={filters} onFiltersChange={updateFilters} onResetFilters={resetFilters} />
          <ExportButton filters={filters} sorting={sorting} />
        </div>
        {total > 0 && <TotalResults total={total} />}
      </div>

      {/* Tabs de estado */}
      <StatusToolbar activeStatus={activeStatus} onStatusChange={handleStatusChange} />

      {/* 🎯 Estado vacío: siempre en el DOM, controlado por opacity y height */}
      <div
        className={`transition-opacity duration-200 overflow-hidden ${
          total === 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
        }`}
      >
        <EmptyView
          type={hasFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          title={
            hasFilters
              ? t('empty.no_results_title', 'No se encontraron resultados')
              : t('empty.title', 'No hay archivos')
          }
          description={
            hasFilters
              ? t(
                  'empty.no_results_description',
                  'No hay archivos que coincidan con los filtros aplicados. Intenta ajustar los filtros.',
                )
              : t('empty.description', 'No se encontraron archivos de courier')
          }
        />
      </div>

      {/* 🎯 Vista de archivos: siempre en el DOM, controlado por opacity y height */}
      <div
        className={`mb-4 transition-opacity duration-200 overflow-hidden ${
          total > 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0'
        }`}
      >
        <CourierFilesAgGridTable
          courierFiles={courierFiles || []}
          columnDefs={columnDefs}
          isLoading={isLoading}
          isFetching={isFetching}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          selectedFiles={selectedFiles}
          onSelectionChanged={handleSelectionChanged}
          onClearSelection={handleClearSelection}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      {/* SelectionToolbar */}
      <SelectionToolbar
        selectedCount={selectedFiles.size}
        onClose={handleClearSelection}
        onConfirmSelected={handleConfirmSelected}
        confirmableCount={confirmableCount}
      />

      {/* Modal de subir archivo */}
      <UploadCourierFileButton isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />

      <BulkInboxCourierFilesModal isOpen={isBulkInboxModalOpen} onClose={() => setIsBulkInboxModalOpen(false)} />

      {team?.id != null && (
        <RunInboxCourierFilesModal
          isOpen={isRunInboxModalOpen}
          onClose={() => setIsRunInboxModalOpen(false)}
          teamId={team.id}
          initialCourierId={filters.courier_id}
          onSuccess={refetch}
        />
      )}

      {/* Modal de marcar como procesado */}
      <MarkProcessedButton
        isOpen={isMarkProcessedModalOpen}
        onClose={handleMarkProcessedClose}
        fileIds={selectedFilesData
          .filter((file) => {
            const normalizedStatus = file.status?.toUpperCase();
            return normalizedStatus === 'IMPORTED';
          })
          .map((file) => file.id)}
      />
    </div>
  );
};
